import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { Menu, Tab } from '@app/interfaces/rm/resources';
import { ResourceExportComponent } from '@app/shared/components/resource-export/resource-export.component';
import { NotificationService } from '@services/lm/notification.service';
import { ResourceDefinitionsService } from '@services/rm/resource-definitions.service';
import { ResourcesService } from '@services/rm/resources.service';
import { SettingsService } from '@services/sys/settings.service';
import { TokenService } from '@services/sys/token.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-resource-menu',
    templateUrl: './resource-menu.component.html',
    styleUrls: ['./resource-menu.component.less'],
})
export class ResourceMenuComponent implements OnInit, OnDestroy {
    @Input() hasPermission = false;
    @Output() navigateTo = new EventEmitter();
    @ViewChild('resourceExport') resourceExport!: ResourceExportComponent;
    private router$!: Subscription;
    categoryMap: {
        [key: string]: string
    } = {
            PHYSICAL: 'Physical',
            ACCESSORY: 'Accessory',
            SOFTWARE: 'Software',
        };
    menus: Menu[] = [];
    tabs: Tab[] = [
        {
            key: 'CHAMBER',
            tab: 'Chamber',
            permissions: ['Chamber View'],
            category: 'PHYSICAL',
        },
        {
            key: 'RADIO',
            tab: 'Radio',
            permissions: ['Radio View'],
            category: 'PHYSICAL',
        },
        {
            key: 'MATRIX',
            tab: 'Matrix',
            permissions: ['Matrix View'],
            category: 'PHYSICAL',
        },
        {
            key: 'SCANNER',
            tab: 'Scanner',
            permissions: ['Scanner View'],
            category: 'PHYSICAL',
        },
        {
            key: 'BASE_STATION',
            tab: 'Base Station',
            permissions: ['Base Station View'],
            category: 'PHYSICAL',
        },
        {
            key: 'UE',
            tab: 'UE',
            permissions: ['UE View'],
            category: 'PHYSICAL',
        },
        {
            key: 'PHYSICAL_SERVER',
            type: 'other',
            tab: 'Physical Server',
            permissions: ['Physical Server View'],
            category: 'PHYSICAL',
        },
        {
            key: 'SERVER_ACCESSORY',
            tab: 'Server Accessory',
            type: 'other',
            permissions: ['Server Accessory View'],
            category: 'ACCESSORY',
        },
        {
            key: 'TEST_EQUIPMENT',
            tab: 'Test Equipment',
            type: 'other',
            permissions: ['Test Equipment View'],
            category: 'ACCESSORY',
        },
        {
            key: 'SFP',
            tab: 'SFP',
            type: 'other',
            permissions: ['SFP View'],
            category: 'ACCESSORY',
        },
        {
            key: 'SWITCH_ROUTER',
            tab: 'Switch Router',
            type: 'other',
            permissions: ['Switch Router View'],
            category: 'PHYSICAL',
        },
        {
            key: 'TEST_SIM',
            tab: 'Test SIM',
            type: 'other',
            permissions: ['Test SIM View'],
            category: 'ACCESSORY',
        },
        {
            key: 'SOFTWARE_TOOL',
            tab: 'Software Tools',
            type: 'other',
            permissions: ['Software Tools View'],
            category: 'SOFTWARE',
        },
        {
            key: 'BBU',
            tab: 'BBU',
            type: 'other',
            permissions: ['BBU View'],
            category: 'PHYSICAL',
        },
        {
            key: 'BBU_ACCESSORY',
            tab: 'BBU Accessory',
            type: 'other',
            permissions: ['BBU Accessory View'],
            category: 'ACCESSORY',
        },
        {
            key: 'RF_ACCESSORY',
            tab: 'RF Accessory',
            type: 'other',
            permissions: ['RF Accessory View'],
            category: 'ACCESSORY',
        },
        {
            key: 'RF_SWITCH_SPLITTER',
            tab: 'RF Switch Splitter',
            type: 'other',
            permissions: ['RF Switch Splitter View'],
            category: 'PHYSICAL',
        },
        {
            key: 'ATTENUATOR',
            tab: 'Attenuator',
            type: 'other',
            permissions: ['Attenuator View'],
            category: 'ACCESSORY',
        },
        {
            key: 'GPS_AMPLIFIER',
            tab: 'GPS Amplifier',
            type: 'other',
            permissions: ['GPS Amplifier View'],
            category: 'ACCESSORY',
        },
        {
            key: 'SHIELD_CHAMBER_BOX',
            tab: 'Shield Chamber Box',
            type: 'other',
            permissions: ['Shield Chamber Box View'],
            category: 'PHYSICAL',
        },
        {
            key: 'RACK',
            tab: 'Rack',
            type: 'other',
            permissions: ['Rack View'],
            category: 'PHYSICAL',
        },
        {
            key: 'CLOUD_CLUSTER_PLATFORM',
            tab: 'Cloud Cluster Platform',
            type: 'other',
            permissions: ['Cloud Cluster Platform View'],
            category: 'SOFTWARE',
        },
        {
            key: 'OCU_ODU',
            tab: 'CNF(VNF)',
            type: 'other',
            permissions: ['CNF(VNF) View'],
            category: 'SOFTWARE',
        },
    ];
    menuKeyword = '';
    pos = 0;
    selectedTab!: Tab;
    confirmVisible = false;
    cancelLoading = false;
    overwriteLoading = false;
    keepLoading = false;
    formData!: FormData | null;
    selectSheets: string[] = [];
    userId!: number;

    constructor(private router: Router,
        private tokenService: TokenService,
        private resourceDefinitionsService: ResourceDefinitionsService,
        private resourcesService: ResourcesService,
        private message: NzMessageService,
        private notification: NzNotificationService,
        private settingService: SettingsService,
        private notificationService: NotificationService) {
    }

    async ngOnInit(): Promise<void> {
        const params = {
            page: 0,
            size: 99999,
            type: 'MODIFIABLE_TYPE'
        };
        const { content } = await this.resourceDefinitionsService.find(params);
        const customTypes = content?.map((item) => {
            return {
                value: item.id,
                tab: item.title,
                type: 'customize',
                permissions: [],
                customId: item.id,
                key: item.title,
                category: item.category
            };
        });
        this.tabs = [...this.tabs, ...customTypes];
        if (this.hasPermission) {
            this.tabs = this.tabs.filter(tab => this.settingService.hasPermission(tab.permissions));
        }
        this.initMenus();
        this.router$ = this.router.events.pipe(filter((e) => e instanceof ActivationEnd)).subscribe(() => this.setActive());
        this.setActive();
        this.navigateTo.emit(this.selectedTab);
        this.userId = this.tokenService.get().id;
    }

    initMenus(): void {
        const menus = this.getMenus();
        this.menus = JSON.parse(JSON.stringify(menus));
    }

    getMenus(menuKeyword: string = this.menuKeyword, tabs: Tab[] = this.tabs): Menu[] {
        const menus = Object.keys(this.categoryMap).map(category => {
            return {
                category: this.categoryMap[category],
                children: tabs.filter(tab => tab.category === category && (tab.tab as string).toLowerCase().includes(menuKeyword.toLowerCase())),
            };
        });
        return menus;
    }

    private setActive(): void {
        const key = this.router.url.substr(this.router.url.lastIndexOf('/') + 1);
        const idx = this.tabs.findIndex((w) => w.key === key || String(w.value) === key);
        if (idx !== -1) {
            this.pos = idx;
        } else {
            this.to(this.tabs[0]);
        }
        this.selectedTab = this.tabs[idx || 0];
    }

    to(item: Tab): void {
        this.navigateTo.emit(item);
    }

    ngOnDestroy(): void {
        this.router$.unsubscribe();
    }

    export(): void {
        this.resourceExport.open(this.menus);
    }

    async import(file: File): Promise<void> {
        const formData = new FormData();
        formData.append('file', file);
        // get file sheets
        const sheetNames = await this.resourcesService.getImportSheet(formData);
        const tabs = this.tabs?.filter(item => (sheetNames || []).includes(item?.key));
        const menus = tabs && tabs.length > 0 ? this.getMenus('', tabs) : [];
        this.resourceExport.open(menus, true);
        this.formData = formData;
        return;
    }

    async getImportResourceType(selectedTab: Tab[]): Promise<void> {
        console.log('selectedTab', selectedTab, this.formData);
        if (selectedTab.length === 0) {
            this.message.warning('Please select one resource');
            return;
        }
        if (!this.formData) {
            return;
        }
        this.selectSheets = selectedTab?.map(item => item?.key);
        const hasSameId = await this.resourcesService.importCheck(this.selectSheets, this.formData);
        if (!hasSameId) {
            this.importFile();
        } else {
            this.confirmVisible = true;
        }
    }

    async importFile(type?: string): Promise<void> {
        if (!this.formData) {
            return;
        }
        await this.notificationService.loadInitial(this.userId);
        await this.resourcesService.import(this.formData, type, this.selectSheets);
        this.notification.create(
            'info',
            'Import started',
            'Import process started in the background. You will be notified upon completion.',
        );
        this.notificationService.startPolling(this.userId);
        this.resourceExport.close();
        this.close();
        this.formData = null;
        // location.reload();
    }

    fileSubmit(type: string): void {
        if (!this.formData) {
            return;
        }
        type === 'CANCEL' ? this.cancelLoading = true :
            (type === 'OVERWRITE' ? this.overwriteLoading = true : this.keepLoading = true);
        this.importFile(type);
    }

    close(): void {
        this.confirmVisible = false;
    }
}
